import { z } from 'zod';
import { TicketEventTypeSchema } from './tickets';
import { AttendantStatusSchema } from './presence';

/**
 * Eventos do bus de realtime (DESIGN-V8 Etapa 2). Payloads MINÚSCULOS de
 * propósito: só ids — o cliente reage invalidando/refetchando as queries;
 * conteúdo nunca viaja pelo bus (nem pelo canal Redis entre réplicas).
 *
 * Todo evento carrega `deskId` porque o stream SSE filtra por visibilidade de
 * mesa no SERVIDOR (mesma resolução do módulo groups): deskId null = evento
 * sem mesa, visível a todos os atendentes conectados do tenant.
 */
const deskId = z.number().int().nullable();

/**
 * v12 — de que LADO veio a mensagem (espelha `chat_messages.direction`):
 * 'inbound' = escrita pelo CLIENTE (widget público ou WhatsApp), a ÚNICA que
 * acende sino/som do atendente; 'outbound' = atendente, bot ou mensagem de
 * sistema — nunca alerta (inclusive a resposta do próprio usuário, que é o
 * caso "não toque para mim mesmo" resolvido sem precisar do ator no bus).
 *
 * OPCIONAL de propósito, e a ausência significa DESCONHECIDO ⇒ NÃO alerta:
 * durante um deploy o web novo pode receber evento de réplica antiga (sem o
 * campo). Assumir 'inbound' faria cada resposta do próprio atendente tocar;
 * assumir 'outbound'/desconhecido cala — que é exatamente o comportamento de
 * hoje (chat sem som) e o degrade seguro. A sincronização de cache não olha
 * este campo, então nada do realtime regride enquanto ele falta.
 *
 * Spec 021: a mensagem do cliente numa conversa "com o bot" (bot externo,
 * antes da transferência) sai SEM o campo, pelo mesmo motivo — quem responde
 * é o bot, e o sino do atendente não é chamado para uma conversa que não
 * espera ninguém da equipe. A sincronização de cache segue igual.
 */
export const ChatMessageDirectionSchema = z.enum(['inbound', 'outbound']);
export type ChatMessageDirection = z.infer<typeof ChatMessageDirectionSchema>;

export const ChatMessageCreatedEventSchema = z.object({
  type: z.literal('chat.message.created'),
  chatId: z.number().int(),
  messageId: z.number().int(),
  deskId,
  direction: ChatMessageDirectionSchema.optional(),
});
export type ChatMessageCreatedEvent = z.infer<typeof ChatMessageCreatedEventSchema>;

/** tick de entrega (sent/delivered/read/failed) ou backfill de mídia */
export const ChatMessageUpdatedEventSchema = z.object({
  type: z.literal('chat.message.updated'),
  chatId: z.number().int(),
  messageId: z.number().int(),
  deskId,
});
export type ChatMessageUpdatedEvent = z.infer<typeof ChatMessageUpdatedEventSchema>;

/** estado/responsável/mesa/leitura mudou — a lista e o detalhe re-buscam */
export const ChatUpdatedEventSchema = z.object({
  type: z.literal('chat.updated'),
  chatId: z.number().int(),
  deskId,
});
export type ChatUpdatedEvent = z.infer<typeof ChatUpdatedEventSchema>;

/**
 * Conversa tirada da mão de um atendente (spec 013 — "assumir de outro" com
 * chat.manage_others). `previousResponsibleId` é o DESTINATÁRIO do aviso, não
 * o ator: o web do atendente anterior compara com o próprio id e mostra o
 * toast; os demais só ignoram (a lista já reage pelo chat.updated).
 */
export const ChatTakenOverEventSchema = z.object({
  type: z.literal('chat.taken_over'),
  chatId: z.number().int(),
  deskId,
  previousResponsibleId: z.number().int(),
});
export type ChatTakenOverEvent = z.infer<typeof ChatTakenOverEventSchema>;

/**
 * Spec 073 (P1): a conversa foi transferida para uma PESSOA. Vai para o tenant
 * inteiro (`deskId` null): quem recebe pode não enxergar a mesa da conversa — o
 * responsável a vê por ser o responsável. Só a aba de `toUserId` reage, e o
 * evento não leva nome de cliente: a aba busca a conversa.
 */
export const ChatTransferredEventSchema = z.object({
  type: z.literal('chat.transferred'),
  chatId: z.number().int(),
  deskId,
  toUserId: z.number().int(),
  byUserId: z.number().int(),
  byName: z.string(),
});
export type ChatTransferredEvent = z.infer<typeof ChatTransferredEventSchema>;

/** carona barata: dois atendentes no mesmo ticket passam a se ver */
export const TicketCommentCreatedEventSchema = z.object({
  type: z.literal('ticket.comment.created'),
  ticketNumber: z.number().int(),
  deskId,
});
export type TicketCommentCreatedEvent = z.infer<typeof TicketCommentCreatedEventSchema>;

/**
 * Evento NOTIFICÁVEL do ticket (v12): o funil emitTicketEvent traduz para cá
 * todo tipo que está em TICKET_EVENT_TO_NOTIFICATION_KEY (created,
 * status_changed, stage_changed, responsible_changed, comment_added) — é o que
 * acende o sino/som na hora, sem esperar o poll.
 *
 * Continua sendo só id + tipo: quem quer conteúdo re-busca
 * /tickets/events/recent, que já traz título do ticket e o ATOR (userId) — por
 * isso o ator NÃO viaja no bus (o cliente decide "fui eu quem causou?" olhando
 * a linha do feed, que é a fonte persistida).
 *
 * `comment_added` gera ESTE evento E o `ticket.comment.created` legado: são
 * consumidores diferentes (o legado invalida a timeline do ticket aberto, este
 * acende o sino) e o legado segue publicado para não quebrar cliente antigo.
 */
export const TicketEventCreatedEventSchema = z.object({
  type: z.literal('ticket.event.created'),
  ticketNumber: z.number().int(),
  deskId,
  eventType: TicketEventTypeSchema,
});
export type TicketEventCreatedEvent = z.infer<typeof TicketEventCreatedEventSchema>;

/**
 * Status de uma pessoa da equipe mudou (spec 015): entrou/saiu de pausa,
 * trocou o motivo, ficou ausente ou teve a pausa encerrada pelo admin.
 * `deskId` é SEMPRE null — status é da pessoa, não da mesa, e o filtro de
 * visibilidade deixa null passar para todo o tenant (desk-filter). `since`
 * vai como ISO: o bus serializa JSON, e o chip do topo calcula o contador
 * a partir dele sem refetch.
 */
export const PresenceChangedEventSchema = z.object({
  type: z.literal('presence.changed'),
  userId: z.number().int(),
  status: AttendantStatusSchema,
  reasonId: z.number().int().nullable(),
  since: z.string().nullable(),
  deskId: z.null(),
});
export type PresenceChangedEvent = z.infer<typeof PresenceChangedEventSchema>;

export const RealtimeEventSchema = z.discriminatedUnion('type', [
  ChatMessageCreatedEventSchema,
  ChatMessageUpdatedEventSchema,
  ChatUpdatedEventSchema,
  ChatTakenOverEventSchema,
  ChatTransferredEventSchema,
  TicketCommentCreatedEventSchema,
  TicketEventCreatedEventSchema,
  PresenceChangedEventSchema,
]);
export type RealtimeEvent = z.infer<typeof RealtimeEventSchema>;
